import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import debounce from 'lodash/debounce';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import LabeledTextbox from '../LabeledTextbox';
import { emailAddressValidator } from '../../../models/schemas/Customer';

export const GuestInfoFormTestId = 'guestinfoform-input';

export type GuestInfoFormData = {
  emailAddress: string;
};
export type GuestInfoFormProps = {
  defaultEmailAddress?: string;
  autoFocus?: boolean;
  onValidData: (formData: GuestInfoFormData) => void;
  onErrorStatus?: (hasError: boolean) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  forceEmailError?: boolean;
};

const GuestInfoForm: React.FC<GuestInfoFormProps> = ({
  defaultEmailAddress,
  onValidData,
  onErrorStatus,
  onChange,
  autoFocus = false,
  forceEmailError = false,
}: GuestInfoFormProps) => {
  const guestInfoFormSchema = yup.object<GuestInfoFormData>().shape({
    emailAddress: emailAddressValidator,
  });

  const {
    register,
    formState: { errors },
    control,
    setError,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(guestInfoFormSchema),
    defaultValues: {
      emailAddress: defaultEmailAddress,
    },
  });

  useEffect(() => {
    if (forceEmailError && !errors.emailAddress?.message) {
      setError(
        'emailAddress',
        { message: 'Required field' },
        { shouldFocus: true }
      );
    }
  }, [forceEmailError, errors.emailAddress?.message, setError]);

  const debouncedOnValidData = debounce((formData: GuestInfoFormData) => {
    onValidData(formData);
  }, 750);

  const handleInputOnBlur = async (
    event: React.FocusEvent<HTMLInputElement>
  ): Promise<void> => {
    const formData: GuestInfoFormData = {
      emailAddress: event.target.value,
    };
    if (
      (await emailAddressValidator.isValid(formData.emailAddress)) &&
      formData.emailAddress !== ''
    ) {
      debouncedOnValidData(formData);
    }
  };

  const debouncedOnErrorStatus = debounce((error: boolean): void => {
    onErrorStatus?.(error);
  }, 250);

  useEffect(() => {
    debouncedOnErrorStatus(!!Object.keys(errors).length);
  }, [errors, onErrorStatus, autoFocus, debouncedOnErrorStatus]);

  return (
    <div>
      <form data-testid={GuestInfoFormTestId}>
        {(process.env.REACT_APP_ENV === 'QA' ||
          process.env.NODE_ENV === 'development') && (
          <DevTool control={control} placement="top-left" />
        )}
        <LabeledTextbox
          className="mb-6"
          name="emailAddress"
          label="Email"
          type="email"
          autoComplete="email"
          errorLabel={errors?.emailAddress?.message}
          defaultValue={defaultEmailAddress}
          onBlur={handleInputOnBlur}
          register={register}
          onChange={onChange}
        />
      </form>
    </div>
  );
};

export default GuestInfoForm;
